// ─────────────────────────────────────────────────────────────────────────
// quickActionLabels — what the buttons inside a dropdown actually say.
//
// THE PROBLEM
// Every quick-action button read "Add More", in the dropdown AND as the modal
// title. "Add More" tells the user nothing: more of what? And on the Edit
// button it was actively wrong — the stored config had `quickEditLabel:
// "Add More"` too, so clicking "Add More" opened an EDIT form.
//
// A button should name the thing it acts on: "Add New Employer", "Edit
// Employer". That is what the user is thinking, and it is the difference
// between a control you have to try and one you can read.
//
// HOW THE NAME IS FOUND
// From the TARGET MODULE the button opens, turned into a readable singular:
//   employers        → Employer
//   locationMasters  → Location Master
//   client_contacts  → Client Contact
// An admin can still type an explicit label; this only decides what happens
// when they have not. No module name is hardcoded here.
// ─────────────────────────────────────────────────────────────────────────

// Words that should not be title-cased into nonsense when they appear inside
// a module key. Deliberately tiny — this is a display nicety, not a dictionary.
const LOWER_WORDS = new Set(['of', 'and', 'the', 'to', 'for', 'in', 'a', 'an']);

// Irregular plurals worth knowing, because the naive "drop the s" rule turns
// them into something visibly wrong on a button.
const IRREGULAR_SINGULARS = {
  addresses: 'address',
  branches: 'branch',
  batches: 'batch',
  categories: 'category',
  companies: 'company',
  countries: 'country',
  entries: 'entry',
  people: 'person',
  statuses: 'status',
};

/**
 * singularize — a module key's singular form.
 *
 * Conservative on purpose: an unknown word that does not clearly look plural
 * is left ALONE. Printing "Addres" or "Statu" on a button is worse than
 * printing a plural, so the rule only fires where it is safe.
 */
export function singularize(word) {
  const w = String(word ?? '').trim();
  if (!w) return '';
  // An ALL-CAPS acronym is never a plural: "VMS" is a name, and stripping its
  // trailing S produces "VM" — a different thing entirely.
  if (w.length > 1 && w === w.toUpperCase() && /[A-Z]/.test(w)) return w;
  const lower = w.toLowerCase();
  if (IRREGULAR_SINGULARS[lower]) return IRREGULAR_SINGULARS[lower];
  // "-ies" → "-y" (categories → category)
  if (/[^aeiou]ies$/i.test(w)) return w.slice(0, -3) + 'y';
  // "-ses"/"-xes"/"-zes"/"-ches"/"-shes" → drop "es"
  if (/(s|x|z|ch|sh)es$/i.test(w)) return w.slice(0, -2);
  // A plain trailing "s", but never "ss" (address) and never a bare "s".
  if (/[^s]s$/i.test(w)) return w.slice(0, -1);
  return w;
}

/**
 * humanizeModule — a module key as a person would write it.
 * "locationMasters" → "Location Master"
 */
export function humanizeModule(moduleKey) {
  const raw = String(moduleKey ?? '').trim();
  if (!raw) return '';
  const words = raw
    // An acronym RUN followed by a word: "MSPRequests" → "MSP Requests".
    // Must run before the camelCase split, which cannot see this boundary
    // because there is no lowercase letter in front of the capital.
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    // camelCase → camel Case
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    // snake_case / kebab-case / dots → spaces
    .replace(/[_\-.]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return '';

  const singularLast = singularize(words[words.length - 1]);
  const all = [...words.slice(0, -1), singularLast];

  return all
    .map((word, index) => {
      const lower = word.toLowerCase();
      // Preserve an ALL-CAPS acronym an admin deliberately used (VMS, MSP).
      if (word.length > 1 && word === word.toUpperCase()) return word;
      if (index > 0 && LOWER_WORDS.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

/**
 * quickCreateLabel — the "add" button's text.
 * An explicitly configured label always wins.
 */
export function quickCreateLabel(field, fallbackModule) {
  const configured = String(field?.quickCreateLabel ?? '').trim();
  // "Add More" is treated as UNSET rather than as a choice: it is the old
  // default that this function exists to replace, and it is stored on live
  // config. Honouring it literally would mean the fix silently did nothing.
  if (configured && configured.toLowerCase() !== 'add more') return configured;
  const name = humanizeModule(field?.quickCreateModule || fallbackModule);
  return name ? `Add New ${name}` : 'Add New';
}

/**
 * quickEditLabel — the "edit" button's text.
 */
export function quickEditLabel(field, fallbackModule) {
  const configured = String(field?.quickEditLabel ?? '').trim();
  // Same reasoning, and here it also fixes a real bug: the stored config had
  // "Add More" on the EDIT button, so clicking "Add More" opened an edit form.
  if (configured && configured.toLowerCase() !== 'add more') return configured;
  const name = humanizeModule(field?.quickEditModule || fallbackModule);
  return name ? `Edit ${name}` : 'Edit';
}

/**
 * quickModalTitle — the popup's heading.
 *
 * The requirement asks for the popup to match the button ("same in the popup
 * also"), so it uses the same resolved name. The heading is allowed to be
 * slightly fuller than the button, because a dialog title has room and the
 * user has just left the context behind.
 */
export function quickModalTitle(mode, field, fallbackModule) {
  const name = humanizeModule(
    (mode === 'create' ? field?.quickCreateModule : field?.quickEditModule) || fallbackModule,
  );
  if (mode === 'create') {
    const configured = String(field?.quickCreateTitle ?? '').trim();
    if (configured) return configured;
    return name ? `Add New ${name}` : quickCreateLabel(field, fallbackModule);
  }
  const configured = String(field?.quickEditTitle ?? '').trim();
  if (configured) return configured;
  return name ? `Edit ${name}` : quickEditLabel(field, fallbackModule);
}
